/**
 * The module catalog, fetched once per browser session.
 *
 * GET /auth/api/v1/rbac/catalog (auth service; nginx and setupProxy route
 * /auth/api/* to it, and every other auth call goes through the shared
 * apiClient the same way) returns modules[] (slug, label, group, core,
 * aliases, ui_sections), module_groups[] and ui_module_lock.
 *
 * - The answer is kept in memory and in sessionStorage, so a reload in the
 *   same tab does not ask again.
 * - Until it lands, the generated copy of the SDK yaml
 *   (config/modules.generated.js) stands in and `ready` is false: callers
 *   that would LOCK something wait for the real answer (the off-switch may
 *   be set), callers that would allow something do not need to.
 * - If the request fails, the generated copy is used with locking on.
 */
import STATIC_CATALOG from '../config/modules.generated';
import { indexCatalog } from './moduleLicense';

export const MODULE_CATALOG_PATH = '/auth/api/v1/rbac/catalog';
const CACHE_KEY = 'saf3ai.moduleCatalog.v1';

const listeners = new Set();
let inFlight = null;

const build = (raw, { source, ready, lockEnabled }) => ({
  raw,
  index: indexCatalog(raw),
  lockEnabled,
  source, // 'static' | 'live' | 'fallback'
  ready,
});

const fromStatic = (ready, source = 'static') => build(STATIC_CATALOG, { source, ready, lockEnabled: true });

/** A catalog response is usable when it lists modules. */
const isUsable = (raw) => Boolean(raw && Array.isArray(raw.modules) && raw.modules.length > 0);

/** `ui_module_lock` defaults to ON when the service does not send it; false is the off-switch. */
const lockFlag = (raw) => raw?.ui_module_lock !== false;

function readCache() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    return isUsable(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeCache(raw) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(raw));
  } catch {
    // private mode / quota: the in-memory copy still serves this page load
  }
}

let state = (() => {
  const cached = typeof sessionStorage !== 'undefined' ? readCache() : null;
  return cached
    ? build(cached, { source: 'live', ready: true, lockEnabled: lockFlag(cached) })
    : fromStatic(false);
})();

const emit = () => listeners.forEach((fn) => { try { fn(state); } catch { /* a listener's bug is its own */ } });

export const getModuleCatalogState = () => state;

export function subscribeModuleCatalog(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Fetch the catalog unless this session already has it. Never rejects. */
export function loadModuleCatalog({ force = false } = {}) {
  if (state.ready && !force) return Promise.resolve(state);
  if (inFlight) return inFlight;
  inFlight = import('../services/api/apiClient')
    .then(({ default: api }) => api.get(MODULE_CATALOG_PATH, { skipModuleLock: true }))
    .then((res) => {
      const raw = res?.data;
      if (!isUsable(raw)) throw new Error('module catalog response lists no modules');
      writeCache(raw);
      state = build(raw, { source: 'live', ready: true, lockEnabled: lockFlag(raw) });
    })
    .catch(() => {
      state = fromStatic(true, 'fallback');
    })
    .then(() => {
      inFlight = null;
      emit();
      return state;
    });
  return inFlight;
}

/** Tests and fixtures: install a catalog as if the service had answered. */
export function primeModuleCatalog(raw = STATIC_CATALOG, { uiModuleLock } = {}) {
  const lockEnabled = uiModuleLock === undefined ? lockFlag(raw) : Boolean(uiModuleLock);
  state = build(raw, { source: 'live', ready: true, lockEnabled });
  inFlight = null;
  emit();
}

/** Tests: back to "not fetched yet", cache cleared. */
export function resetModuleCatalog() {
  try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
  state = fromStatic(false);
  inFlight = null;
  emit();
}

export { STATIC_CATALOG };
